import { useQueryClient } from '@tanstack/react-query';
import type { LoginPolicy } from '@xiltrion/entidad';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@xiltrion/ui/card';
import { Field, FieldContent, FieldDescription, FieldTitle } from '@xiltrion/ui/field';
import { avisar } from '@xiltrion/ui/sonner';
import { Switch } from '@xiltrion/ui/switch';
import { apiPut, errorMessage } from '@/api/client';
import { Cambio, useEntitySettings } from './EntityProfileTab';

/**
 * Política de ingreso de la entidad (GUIA-AGENTES.md §3.11): la aplica la suite al entrar a cualquier app de la
 * entidad. Las apps conectadas no tienen política propia.
 */
export function LoginPolicyCard({ slug, hasIdp }: { slug: string; hasIdp: boolean }) {
  const qc = useQueryClient();
  const q = useEntitySettings(slug);
  if (!q.data) return null;
  const policy = q.data.loginPolicy;
  const save = (patch: Partial<LoginPolicy>) => void apiPut(`/entities/${slug}/settings/login-policy`, { ...policy, ...patch })
    .then(() => { avisar.success('Política guardada. Se aplica desde el próximo ingreso a una app.'); return qc.invalidateQueries({ queryKey: ['entity-settings', slug] }); }, (e) => avisar.error(errorMessage(e)));
  return (
    <Card>
      <CardHeader>
        <CardTitle>Política de ingreso</CardTitle>
        <CardDescription>Cómo pueden entrar las personas de la entidad a sus apps. No aplica a la administración de Datalog. <Cambio info={q.data.changed.login_policy} /></CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field orientation="horizontal">
          <FieldContent>
            <FieldTitle>Permitir el ingreso con contraseña</FieldTitle>
            <FieldDescription>{hasIdp ? 'Apagado, solo se entra con la cuenta institucional configurada abajo.' : 'Para apagarlo, configura y activa antes un ingreso institucional.'}</FieldDescription>
          </FieldContent>
          <Switch aria-label="Permitir el ingreso con contraseña" checked={policy.allowPassword} disabled={policy.allowPassword && !hasIdp} onCheckedChange={(v) => save({ allowPassword: v })} />
        </Field>
        <Field orientation="horizontal">
          <FieldContent>
            <FieldTitle>Exigir verificación en dos pasos</FieldTitle>
            <FieldDescription>Quien entra con contraseña debe tener activa la app autenticadora. El ingreso institucional sigue la política de su proveedor.</FieldDescription>
          </FieldContent>
          <Switch aria-label="Exigir verificación en dos pasos" checked={policy.requireMfa} onCheckedChange={(v) => save({ requireMfa: v })} />
        </Field>
      </CardContent>
    </Card>
  );
}
